import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { checkUserRole } from "@/lib/userUtils";

interface GlobalAdminGuardProps {
  children: React.ReactNode;
}

export const GlobalAdminGuard = ({ children }: GlobalAdminGuardProps) => {
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let isMounted = true;

    (async () => {
      try {
        const isGlobalAdmin = await checkUserRole("global_admin");
        if (!isMounted) return;

        if (!isGlobalAdmin) {
          // Non-admins trying to hit an admin route get sent back to dashboard
          navigate("/dashboard", { replace: true, state: { from: location.pathname } });
          setAllowed(false);
        } else {
          setAllowed(true);
        }
      } catch (error) {
        console.error("Error checking global admin role:", error);
        if (isMounted) {
          navigate("/dashboard", { replace: true });
          setAllowed(false);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [navigate, location.pathname]);

  if (allowed === null) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto" />
          <p className="mt-3 text-muted-foreground text-sm">Checking admin access...</p>
        </div>
      </div>
    );
  }

  if (!allowed) {
    return null;
  }

  return <>{children}</>;
};

